import type { Language, WerkverzamelingReason } from '@eformulieren/engine'
import type { CriterionOutcome, ProjectionSection, Field, FieldId } from '@eformulieren/model'
import { Fragment, type ReactNode } from 'react'
import { type PresenceSource, PresenceBar } from '../collab/PresenceBar.js'
import { InfoIcon } from '../icons/InfoIcon.js'
import { SlotIcon } from '../icons/SlotIcon.js'
import { readableBlockName } from '../field/controls/dossierValue.js'
import { inLanguage } from '../language/text.js'
import { fullWidth } from '../field/registry.js'
import { QuestionHistory } from '../field/controls/Subgrid.js'
import { verdictLabel } from '../field/controls/Criterion.js'
import type { QuestionHistoryEntry } from './history.js'
import { AdviceSpot } from './AdviceSpot.js'
import type { AdviceTarget } from './adviceModel.js'
import type { ShareAnchor } from './shareModel.js'
import {
  DesignatedQuestion,
  EarlierValue,
  InternalMarker,
  DifferenceMarker,
  PreviousResult,
  PrefillMarker,
  DossierMarker,
} from './difference.js'

/**
 * Eén sectie: de dragende eenheid van het formulier (R-01).
 *
 * In- en uitklapbaar (R-02), met de uitleg permanent zichtbaar erboven (D-03,
 * R-10) en één of twee kolommen (R-11). De kop is een knop met `aria-expanded` en
 * `aria-controls`: dat is het patroon dat een schermlezer aankondigt en dat met
 * het toetsenbord werkt zonder eigen toetsafhandeling (R-139, R-142).
 *
 * Het paneel wordt met het `hidden`-attribuut weggehaald en niet met CSS
 * verborgen: een schermlezer en de tabvolgorde moeten het uitgeklapte deel
 * overslaan, en `display: none` via een klasse zou dat aan de styling ophangen.
 */

export interface SectionPanelProps {
  readonly sectie: ProjectionSection
  readonly taal: Language
  readonly open: boolean
  readonly onSwitch: (sectionId: string) => void
  /** De nummers van de zichtbare vragen in deze sectie (R-04). */
  readonly numbers: ReadonlyMap<FieldId, number>
  /**
   * Hoe een veld wordt getekend. Het skelet kent de indeling en de nummering; wat
   * er in een veld staat komt van de veldschil en de controls (M2.4 tot M2.9).
   *
   * Het vierde argument zijn de id's van de markeringen die dit paneel al naast het veld heeft
   * getekend (voorvuld, alleen-intern); de aanroeper geeft ze door aan `FieldShell`'s
   * `extraDescription` zodat ze in `aria-describedby` terechtkomen. Zonder dat kanaal staan ze
   * alleen in de visuele leesorde naast het veld (WCAG 1.3.1, M8.12).
   */
  readonly renderField: (
    field: Field,
    sectionNumber: number | undefined,
    sectionId: string,
    markerIds: readonly string[],
    /** Of dit een overgenomen veld is onder een gedeelde bronregel (R-58): dan geen eigen markering. */
    inSourceGroup?: boolean,
  ) => ReactNode
  readonly zichtbaarVeld: (fieldId: FieldId) => boolean
  /**
   * De naam van een bronblok voor de gedeelde bronregel boven een reeks overgenomen velden (besluit
   * van 30 oktober 2026, R-58). Zonder deze functie de leesbare vorm van de slug.
   */
  readonly bronNaamVan?: (contract: string) => string
  /**
   * Waarom deze sectie in de werkverzameling staat (R-98), of niets.
   *
   * Drie waarden en geen vierde voor "geen bijzondere reden": een sectie zonder reden staat
   * niet in de werkverzameling en krijgt dus geen badge. Zo blijft de badge iets zeggen.
   */
  readonly reden?: WerkverzamelingReason
  /** De opmerkingen van NWO bij deze sectie (R-75). */
  readonly comments?: readonly { veldId: string; tekst: string }[]
  /** De velden waarvan het antwoord anders is dan in de vorige beurt (R-178a). */
  readonly changedFields?: ReadonlySet<string>
  /** De velden die deze sessie zijn voorgevuld vanuit de querystring (R-37). */
  readonly prefilled?: ReadonlySet<string>
  /**
   * De velden die niet voor externen zichtbaar zijn, alleen gevuld in de interne weergave
   * (R-170, D-54). `Formulier.tsx` berekent hem uit `veld.externZichtbaar` en de actieve modus;
   * dit paneel kent de modus zelf niet en toont simpelweg wat erin staat.
   */
  readonly onlyInternal?: ReadonlySet<string>
  /** De vorige uitslag per criterium, alleen voor een toetser (R-178a, R-187). */
  readonly vorigeUitslag?: ReadonlyMap<string, CriterionOutcome>
  /**
   * De antwoorden uit de gekozen eerdere versie, per veld (R-96).
   *
   * Een `Map` en geen object: het onderscheid tussen "stond er leeg in" en "stond er niet in"
   * moet blijven bestaan, en `has` zegt dat waar een objecttoegang dat verliest.
   */
  readonly eerdereWaarden?: ReadonlyMap<string, unknown>
  /** De kop bij die antwoorden; zie `EarlierValue`. */
  readonly eerdereWaardenLabel?: Readonly<Record<'nl' | 'en', string>>
  /**
   * De eerdere antwoorden per criterium-identiteit, alleen in de modus met historie (R-137, R-62).
   *
   * Uit `historyPerQuestion`: de groepen van de reeksen in de projectie, op de waarde van hun
   * vraag-eigenschap. Zonder deze kaart, of zonder sleutel voor een criterium, staat er niets bij.
   */
  readonly historie?: ReadonlyMap<string, readonly QuestionHistoryEntry[]>
  /**
   * De naam van elke vraag in het formulier, voor een criterium dat er één aanwijst (D-55).
   *
   * Over alle secties heen, want de vraag waarover een criterium gaat staat per definitie in een
   * andere sectie dan het criterium zelf. Deze sectie kan hem dus niet zelf opzoeken.
   */
  readonly vraagnamen?: ReadonlyMap<string, string>
  /**
   * Wie er nu in deze sectie werkt (R-72), of niets.
   *
   * Per sectie en niet per formulier, want dat is de eenheid waar de awareness op staat: één
   * sectiedocument is één verbinding. "Wie werken hier nu" gaat dus precies over deze sectie, en
   * een balk boven het formulier zou een vraag beantwoorden die niemand stelt.
   */
  readonly aanwezigheid?: PresenceSource
  /** Hoe een deelnemer in de presence-balk heet; zie `FormProps.presenceName`. */
  readonly presenceName?: (person: string) => string
  /**
   * Hoeveel opmerkingdraadjes er in deze sectie nog openstaan (R-73), nul als er geen zijn.
   *
   * **Waarom dit getal er is.** Een openstaand draadje hoort te worden afgehandeld in de beurt
   * waarin het is ontstaan: over een beurtwisseling reist het niet mee (ontwerp 4.6, M4.19). Dan
   * moet het wel opvallen vóór het sluiten, en tot nu stond het aantal alleen in het draadpaneel
   * onder het veld zelf, dus in een dichtgeklapte sectie zag niemand het.
   *
   * **Geen badge maar een teller, en dat is met opzet.** De badge `reden` heeft drie waarden en
   * geen vierde, om de reden die daar staat; een tweede badge ernaast laat een lezer zoeken naar
   * het verschil. Dit is een aantal en geen reden, dus het staat er als tekst in de rustige
   * kleur van een bijzin.
   */
  readonly openstaandeOpmerkingen?: number
  /** De eigen persoonssleutel voor de presence-balk; zie `PresenceBar`. */
  readonly presenceSelf?: string
  /**
   * Of dit de sectie is waar de inhoudsopgave nu op wijst (`useActiveSection` in
   * `Formulier.tsx`), voor een gekleurde kaartrand (c3/d1, R-157).
   *
   * Optioneel, en zonder `Formulier.tsx` eromheen (een geïsoleerde test van dit paneel) blijft de
   * rand vanzelf weg, net als vóór deze opdracht.
   */
  readonly active?: boolean
  /**
   * Of deze sectie zegt dat ze staat zoals de aanvrager haar indiende (de toetsgedaante, ontwerp
   * van het toetsscherm, 29 oktober 2026). De velden zijn dan al alleen-lezen; deze zin zegt het in
   * woorden, zodat het geen kapotte invoer lijkt (R-133).
   */
  readonly readOnlyNote?: boolean
  /**
   * Een leessectie voor wie verder ook schrijft of meeleest (audit delen G-16, mockup 07, WCAG 1.3.1):
   * "Alleen lezen" in de knop van de kop, zodat het bij de naam hoort die een schermlezer voorleest
   * en niet alleen een grijze kleur is, en een zin eronder. `opmerkingen` zegt of er een opmerking
   * te plaatsen is (besluit 8 van 30 oktober 2026). Alleen in de invulgedaanten; bij nalezen en in de
   * toets zegt het formulier het al op een andere manier.
   */
  readonly alleenLezen?: { readonly opmerkingen: boolean }
  /**
   * Wat er naast de klapknop in de kopregel staat (de ballon voor een opmerking bij de sectie, R-73),
   * nooit erin: een knop in een knop is geen knop meer voor een schermlezer.
   */
  readonly kopExtra?: ReactNode
  /** Wat bovenaan in de sectie staat, onder de kop: de opmerkingen bij de sectie als geheel (R-73). */
  readonly bovenaan?: ReactNode
  /**
   * Advies bij deze sectie (R-184, ontwerp 7.4a): de tekstballon en het label "1 advies" bij de kop en
   * bij elke vraag, en de markering van het anker. Zonder deze prop staat er niets van advies en is de
   * kop precies wat hij was (R-184b: de externe kant krijgt hem nooit).
   */
  readonly advies?: SectionAdvice
}

/** Wat een sectie over advies weet; `Form` maakt het per sectie uit {@link AdviceMarks}. */
export interface SectionAdvice {
  /** Of hier advies te vragen is: de kijker kan vragen en de sectie is niet uitgesloten. */
  readonly vragen: boolean
  readonly aantal: (target: AdviceTarget) => number
  readonly onVragen: (anchor: ShareAnchor) => void
  readonly onToon: (target: AdviceTarget) => void
  /** Het gemarkeerde anker in deze sectie: een veld-id, of de sectie zelf. */
  readonly gemarkeerd?: string
  /** Het anker van de vraag op de adviseurpagina: een veld-id, of de sectie zelf. */
  readonly vast?: string
}

/** De waarde van {@link SectionAdvice.gemarkeerd} en `vast` als de sectie zelf het anker is. */
export const WHOLE_SECTION = '*sectie'

export function SectionPanel({
  sectie,
  taal: language,
  open,
  onSwitch,
  numbers,
  renderField,
  bronNaamVan: sourceNameOf = readableBlockName,
  zichtbaarVeld: visibleField,
  reden: reason,
  comments,
  changedFields,
  prefilled,
  onlyInternal,
  vorigeUitslag: previousResult,
  eerdereWaarden: earlierValues,
  eerdereWaardenLabel: earlierLabel,
  historie: history,
  vraagnamen: questionNames,
  aanwezigheid: presence,
  presenceName,
  presenceSelf,
  openstaandeOpmerkingen: outstandingComments = 0,
  active = false,
  readOnlyNote = false,
  alleenLezen: readOnly,
  kopExtra: headerExtra,
  bovenaan: top,
  advies: advice,
}: SectionPanelProps): ReactNode {
  const panelId = `${sectie.id}-paneel`
  // Wie de sectie niet kan bewerken, leest haar: dan is "aan te passen" bij een veld van het
  // processysteem niet waar voor deze lezer.
  const reading = sectie.recht !== 'bewerken'
  const headerId = `${sectie.id}-kop`
  const sectionTitle = inLanguage(sectie.titel, language)
  const marked = (key: string) => advice !== undefined && (advice.gemarkeerd === key || advice.vast === key)
  // De naam van de knop als zin, met komma's tussen titel, badge, alleen-lezen en het aantal open
  // opmerkingen ("Overzicht, 2 open opmerkingen"). Zonder die naam las Chromium "Overzicht 2 open
  // opmerkingen", en een verborgen komma in een absoluut gepositioneerd tekstdeel wordt " , " (besluit
  // van 1 november 2026 over tellingen, R-139).
  const headerParts = [
    sectionTitle,
    reason === undefined ? undefined : inLanguage(REASON_NAME[reason], language),
    readOnly === undefined ? undefined : inLanguage(READ_ONLY_LABEL, language),
    outstandingComments === 0 ? undefined : inLanguage(COUNTER_NAME(outstandingComments), language),
  ].filter((part): part is string => part !== undefined)
  const header = (
      <h2 className="ef-sectie__kop" id={headerId}>
        <button
          type="button"
          className="ef-sectie__knop"
          aria-expanded={open}
          aria-controls={panelId}
          aria-label={headerParts.length === 1 ? undefined : headerParts.join(', ')}
          onClick={() => onSwitch(sectie.id)}
        >
          <span className="ef-sectie__pijl" aria-hidden="true" />
          <span className="ef-sectie__titel">{inLanguage(sectie.titel, language)}</span>
          {reason === undefined ? null : (
            // Als tekst en niet als kleur: kleur is nooit de enige drager van informatie
            // (R-139). De badge staat in de knop, zodat hij ook bij een dichtgeklapte sectie
            // meegaat met de naam die de schermlezer voorleest.
            <span className="ef-sectie__badge" data-reden={reason}>
              {inLanguage(REASON_NAME[reason], language)}
            </span>
          )}
          {readOnly === undefined ? null : (
            // In de knop, zodat het bij de naam van de kop hoort (WCAG 1.3.1); de komma ervoor staat in
            // de naam van de knop (`headerParts`).
            <span className="ef-sectie__alleenlezenlabel">
              {inLanguage(READ_ONLY_LABEL, language)}
            </span>
          )}
          {outstandingComments === 0 ? null : (
            // In de knop en als tekst, om dezelfde twee redenen als de badge: hij gaat mee met
            // de naam die de schermlezer voorleest, ook als de sectie dicht is, en kleur is
            // nooit de enige drager (R-139).
            <span className="ef-sectie__opmerkingteller">
              {inLanguage(COUNTER_NAME(outstandingComments), language)}
            </span>
          )}
        </button>
      </h2>
  )
  return (
    <section
      className="ef-sectie"
      aria-labelledby={headerId}
      data-sectietype={sectie.type}
      data-actief={active ? 'true' : undefined}
      data-advies={marked(WHOLE_SECTION) ? 'gemarkeerd' : undefined}
      id={sectie.id}
    >
      {presence === undefined ? null : (
        // Wie hier werkt, op de bovenrand van de kaart en niet in het paneel (audit van het formulier):
        // daar duwde de regel de inhoud omlaag zodra iemand binnenkwam, onder de handen van wie aan het
        // typen was, en hij verdween met het paneel als de sectie dicht was. Het vak heeft geen hoogte;
        // de regel zweeft erin (`.ef-sectie__aanwezig` in schil.css). In de DOM vóór de kop, zodat een
        // schermlezer hem vóór de inhoud van de sectie leest en de kop zijn naam houdt.
        <div className="ef-sectie__aanwezig">
          <PresenceBar
            awareness={presence}
            taal={language}
            {...(presenceName === undefined ? {} : { nameOf: presenceName })}
            {...(presenceSelf === undefined ? {} : { ik: presenceSelf })}
          />
        </div>
      )}
      {advice === undefined && headerExtra === undefined ? (
        header
      ) : (
        // De ballon en het label naast de knop van de kop en niet erin: een knop in een knop is geen
        // knop meer voor een schermlezer, en de kop moet blijven voorlezen wat hij voorlas.
        <div className="ef-sectie__kopregel">
          {header}
          {headerExtra}
          {advice === undefined ? null : <AdviceSpot
            taal={language}
            naam={sectionTitle}
            aantal={advice.aantal({ sectieId: sectie.id })}
            vragen={advice.vragen}
            vast={advice.vast === WHOLE_SECTION}
            onVragen={() => advice.onVragen({ soort: 'sectie', sectieId: sectie.id })}
            onToon={() => advice.onToon({ sectieId: sectie.id })}
          />}
        </div>
      )}
      <div className="ef-sectie__paneel" id={panelId} hidden={!open}>
        {readOnlyNote ? <p className="ef-sectie__alleenlezen">{inLanguage(READ_ONLY_NOTE, language)}</p> : null}
        {readOnly === undefined ? null : (
          <p className="ef-sectie__alleenlezen">{inLanguage(readOnly.opmerkingen ? READ_ONLY_SHARED_COMMENT : READ_ONLY_SHARED, language)}</p>
        )}
        {top}
        {sectie.uitleg === undefined ? null : (
          <p className="ef-sectie__uitleg">
            <InfoIcon aria-hidden="true" />
            {inLanguage(sectie.uitleg, language)}
          </p>
        )}
        {comments === undefined || comments.length === 0 ? null : (
          /**
           * De opmerking van NWO, boven de vragen waar ze over gaat (R-75).
           *
           * Boven en niet naast: de opmerking gaat over de sectie als geheel, en naast een
           * vraag zou hij een kolom kosten die op een tablet niet past (R-158, T14). Een
           * `aside` met een eigen kop, zodat een schermlezergebruiker hem kan overslaan en
           * er ook naartoe kan springen.
           */
          <aside className="ef-sectie__opmerking" aria-label={inLanguage(COMMENT_HEADER, language)}>
            <h3 className="ef-sectie__opmerkingkop">{inLanguage(COMMENT_HEADER, language)}</h3>
            {comments.map((comment) => (
              <p className="ef-sectie__opmerkingtekst" key={comment.veldId}>
                {comment.tekst}
              </p>
            ))}
          </aside>
        )}
        <div className="ef-sectie__velden" data-kolommen={sectie.kolommen}>
          {sectie.velden
            .filter((field) => visibleField(field.id))
            .map((field, place, visible) => {
              // Eén bronregel boven twee of meer overgenomen velden uit hetzelfde blok na elkaar, in
              // plaats van "Alleen-lezen" en "uit …" bij elk veld (besluit van 30 oktober 2026,
              // R-58). Een los overgenomen veld tussen eigen velden houdt zijn eigen markering. Voor
              // velden van het processysteem geldt hetzelfde: één markering boven een reeks velden
              // die op dezelfde manier overgenomen zijn (alleen-lezen, of aanpasbaar).
              const source = groupOf(field)
              const sameAs = (other: Field | undefined): boolean => other !== undefined && source !== undefined && groupOf(other) === source
              const grouped = source !== undefined && (sameAs(visible[place - 1]) || sameAs(visible[place + 1]))
              const groupStart = grouped && !sameAs(visible[place - 1])
              const groupLineId = `ef-bronregel-${(() => {
                let start = place
                while (start > 0 && sameAs(visible[start - 1])) start -= 1
                return visible[start]?.id ?? field.id
              })()}`
              const result =
                field.control === 'criterium' ? previousResult?.get(field.criteriumId) : undefined
              const resultLabel = result?.oordeel === undefined ? undefined : verdictLabel(field, result.oordeel.oordeel, language)
              const changedId = `ef-verschil-${field.id}`
              const prefilledId = `ef-voorvuld-${field.id}`
              const onlyInternalId = `ef-alleen-intern-${field.id}`
              const dossierField = 'dossierveld' in field ? field.dossierveld : undefined
              const dossierId = `ef-dossierveld-${field.id}`
              // Alleen de id's van markeringen die ook echt getekend worden: een niet-bestaand
              // id in `aria-describedby` is net zo stil fout als een ontbrekend id, en dus niet
              // te onderscheiden van deze bijgewerkte lijst zelf.
              const markerIds = [
                changedFields?.has(field.id) === true ? changedId : undefined,
                prefilled?.has(field.id) === true ? prefilledId : undefined,
                onlyInternal?.has(field.id) === true ? onlyInternalId : undefined,
                dossierField === undefined || grouped ? undefined : dossierId,
                grouped ? groupLineId : undefined,
              ].filter((id): id is string => id !== undefined)
              return (
                <Fragment key={field.id}>
                {groupStart && source !== undefined && dossierField !== undefined ? (
                  <p className="ef-dossierregel" id={groupLineId}>
                    <DossierMarker taal={language} id={`${groupLineId}-markering`} aanpasbaar={dossierField.aanpasbaar} lezen={reading} />
                  </p>
                ) : groupStart && source !== undefined ? (
                  <p className="ef-bronregel" id={groupLineId}>
                    <SlotIcon aria-hidden="true" />
                    {inLanguage(SOURCE_LINE(sourceNameOf(source.slice(SOURCE_PREFIX.length))), language)}
                  </p>
                ) : null}
                <div
                  className="ef-veldplek"
                  data-advies={marked(field.id) ? 'gemarkeerd' : undefined}
                  data-gewijzigd={changedFields?.has(field.id) === true ? 'ja' : undefined}
                  data-voorvuld={prefilled?.has(field.id) === true ? 'ja' : undefined}
                  data-alleen-intern={onlyInternal?.has(field.id) === true ? 'ja' : undefined}
                  // Een lang antwoord krijgt de volle breedte, ook in twee kolommen (R-11).
                  data-breed={fullWidth(field.control) ? 'ja' : undefined}
                >
                  {advice === undefined ? null : (
                    <AdviceSpot
                      taal={language}
                      naam={fieldName(field, language)}
                      aantal={advice.aantal({ sectieId: sectie.id, veldId: field.id })}
                      vragen={advice.vragen}
                      vast={advice.vast === field.id}
                      onVragen={() => advice.onVragen({ soort: 'veld', sectieId: sectie.id, veldId: field.id, label: fieldName(field, language), ...(field.control === 'criterium' ? { criterium: true } : {}) })}
                      onToon={() => advice.onToon({ sectieId: sectie.id, veldId: field.id })}
                    />
                  )}
                  {changedFields?.has(field.id) === true ? (
                    <DifferenceMarker taal={language} id={changedId} />
                  ) : null}
                  {prefilled?.has(field.id) === true ? (
                    <PrefillMarker taal={language} id={prefilledId} />
                  ) : null}
                  {onlyInternal?.has(field.id) === true ? (
                    <InternalMarker taal={language} id={onlyInternalId} />
                  ) : null}
                  {dossierField === undefined || grouped ? null : (
                    <DossierMarker taal={language} id={dossierId} aanpasbaar={dossierField.aanpasbaar} lezen={reading} />
                  )}
                  {field.control === 'criterium' && field.hoortBijVeld !== undefined ? (
                    <DesignatedQuestion
                      naam={questionNames?.get(field.hoortBijVeld) ?? field.hoortBijVeld}
                      taal={language}
                    />
                  ) : null}
                  {result === undefined ? null : (
                    <PreviousResult
                      uitkomst={result}
                      taal={language}
                      {...(resultLabel === undefined ? {} : { label: resultLabel })}
                      tweekeuze={field.control === 'criterium' && field.schaal.soort === 'tweekeuze'}
                    />
                  )}
                  {/*
                    Het antwoord uit de gekozen eerdere versie, bij de vraag waar het over gaat
                    (R-96). `has` en niet `get() !== undefined`: een veld dat eerder leeg was
                    hoort ook te zeggen dat het leeg was, en dat is iets anders dan een veld dat
                    niet in die versie voorkwam.
                  */}
                  {earlierValues?.has(field.id) === true ? (
                    <EarlierValue waarde={earlierValues.get(field.id)} veld={field} taal={language} {...(earlierLabel === undefined ? {} : { kop: earlierLabel })} />
                  ) : null}
                  {/*
                    De antwoorden uit eerdere instanties, bij de vraag waar ze over gaan (R-137). Een
                    regio per reeks, want twee reeksen over hetzelfde criterium komen van twee
                    plekken en horen niet in één tabel.
                  */}
                  {field.control === 'criterium'
                    ? history?.get(field.criteriumId)?.map((entry, index) => (
                        <QuestionHistory
                          // Bron en plek samen: twee reeksen uit hetzelfde blok kunnen er naast elkaar staan.
                          // eslint-disable-next-line react/no-array-index-key
                          key={`${entry.bron}-${index}`}
                          taal={language}
                          vraag={inLanguage(field.label, language)}
                          bron={entry.bron}
                          kolommen={entry.kolommen}
                          rijen={entry.rijen}
                        />
                      ))
                    : null}
                  {renderField(field, numbers.get(field.id), sectie.id, markerIds, grouped && dossierField === undefined)}
                </div>
                </Fragment>
              )
            })}
        </div>
      </div>
    </section>
  )
}

/**
 * Waarom een sectie in de werkverzameling staat, in beide talen (R-98, R-143).
 *
 * De drie waarden van R-98 en geen vierde. `beide` is niet "openstaand plus gewijzigd" in twee
 * badges maar één woord, want twee badges naast elkaar laten een lezer zoeken naar het verschil
 * tussen "en" en "of".
 */
const REASON_NAME: Record<WerkverzamelingReason, { nl: string; en: string }> = {
  'openstaand-criterium': { nl: 'Aanpassing gevraagd', en: 'Changes requested' },
  gewijzigd: { nl: 'Gewijzigd', en: 'Changed' },
  beide: { nl: 'Aanpassing gevraagd en gewijzigd', en: 'Changes requested and changed' },
}

/**
 * Het aantal openstaande opmerkingen, met het juiste enkelvoud (R-73, R-143).
 *
 * Voluit en niet als los cijfer: "2" naast een sectietitel laat een schermlezer een getal
 * voorlezen zonder te zeggen wat het is.
 */
const COUNTER_NAME = (count: number): { nl: string; en: string } => ({
  nl: count === 1 ? '1 open opmerking' : `${count} open opmerkingen`,
  en: count === 1 ? '1 open comment' : `${count} open comments`,
})

const COMMENT_HEADER = { nl: 'Opmerking van NWO', en: 'Comment from NWO' }

const READ_ONLY_LABEL = { nl: 'Alleen lezen', en: 'Read only' }
const READ_ONLY_SHARED = { nl: 'U kunt deze sectie lezen, niet wijzigen.', en: 'You can read this section, not change it.' }
const READ_ONLY_SHARED_COMMENT = {
  nl: 'U kunt deze sectie lezen, niet wijzigen. Een opmerking plaatsen kan wel: selecteer de tekst waar ze over gaat.',
  en: 'You can read this section, not change it. You can post a comment: select the text it is about.',
}

const READ_ONLY_NOTE = {
  nl: 'Zoals de aanvrager het indiende. U kunt hier niet in schrijven.',
  en: 'As the applicant submitted it. You cannot write here.',
}

const SOURCE_PREFIX = 'bron:'

/**
 * De groep waartoe een overgenomen veld hoort, of niets voor een eigen veld: het blok van een
 * verwijzing, of voor een veld van het processysteem of het aanpasbaar is.
 */
function groupOf(field: Field): string | undefined {
  if ('dossierveld' in field && field.dossierveld !== undefined) return `dossier:${field.dossierveld.aanpasbaar ? 'aanpasbaar' : 'vast'}`
  if (field.control !== 'verwijzing') return undefined
  const source = 'bron' in field ? field.bron : undefined
  return source === undefined || source === null ? undefined : `${SOURCE_PREFIX}${source.contract}`
}

/** De bronregel boven een groep overgenomen velden (R-58). */
const SOURCE_LINE = (name: string): { nl: string; en: string } => ({
  nl: `Uit ${name}, niet te wijzigen`,
  en: `From ${name}, cannot be changed`,
})

/** De naam van een vraag zoals de lezer hem ziet; een veld zonder label heet "deze vraag", nooit naar zijn id. */
function fieldName(field: Field, language: Language): string {
  return field.label === undefined ? inLanguage({ nl: 'deze vraag', en: 'this question' }, language) : inLanguage(field.label, language)
}
